import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronDown, ChevronRight, CreditCard, FileText } from 'lucide-react';
import apiService from '../services/api';
import { formatDateOnly } from '../utils/dateOnly';
import { getInvoiceAmountDue } from '../utils/invoiceAmountDue';

type PendingInvoiceRow = {
  id: string;
  appointment_id?: string | null;
  status: string;
  total: string | number;
  due?: string | number;
  deposit?: string | number;
  invoice_date?: string;
  due_date?: string;
  sent_at?: string | null;
  last_resent_at?: string | null;
  appointment_scheduled_at?: string | null;
  amount_due?: number;
  onlinePaymentsEnabled?: boolean;
  business?: {
    name?: string;
    business_name?: string;
    short_name?: string;
    shortName?: string;
  } | null;
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => safeNum(v).toFixed(2);

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const formatLogDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatAppointmentWhen = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} @ ${timePart}`;
};

const getBusinessName = (row: PendingInvoiceRow): string => {
  const b = row.business;
  return String(b?.name ?? b?.business_name ?? '').trim() || 'Business';
};

const getBusinessShortName = (row: PendingInvoiceRow): string => {
  const b = row.business;
  return String(b?.shortName ?? b?.short_name ?? '').trim();
};

const BusinessNameLink: React.FC<{ invoice: PendingInvoiceRow; className?: string }> = ({
  invoice,
  className = 'text-sm font-semibold text-[#0B4778] hover:underline transition-colors',
}) => {
  const businessName = getBusinessName(invoice);
  const shortName = getBusinessShortName(invoice);
  if (shortName) {
    return (
      <Link to={`/${shortName}`} className={className} title={`View ${businessName}`}>
        {businessName}
      </Link>
    );
  }
  return <span className="text-sm font-semibold text-[#0B4778]">{businessName}</span>;
};

const getAmountDue = (row: PendingInvoiceRow): number => {
  if (row.amount_due != null && Number.isFinite(Number(row.amount_due))) {
    return Math.max(0, Number(row.amount_due));
  }
  return getInvoiceAmountDue(row);
};

const PendingInvoiceDetail: React.FC<{ invoice: PendingInvoiceRow }> = ({ invoice }) => {
  const navigate = useNavigate();
  const amountDue = getAmountDue(invoice);
  const sentAt = invoice.sent_at ?? invoice.invoice_date ?? null;
  const resentAt = invoice.last_resent_at ?? null;
  const appointmentWhen = formatAppointmentWhen(invoice.appointment_scheduled_at);

  const actionButtons = (
    <>
      <button
        type="button"
        className="aacore-btn-primary justify-center inline-flex items-center gap-2 w-full sm:w-auto"
        disabled={invoice.onlinePaymentsEnabled !== true}
        title={
          invoice.onlinePaymentsEnabled !== true
            ? 'Online payments are not enabled for this business. See the invoice note for payment instructions.'
            : undefined
        }
        onClick={() => {
          if (invoice.onlinePaymentsEnabled !== true) return;
          navigate(`/dashboard/customer/invoices/${invoice.id}/pay`);
        }}
      >
        <CreditCard className="w-4 h-4" />
        Pay now
      </button>
      <Link
        to={`/dashboard/customer/invoices/${invoice.id}`}
        className="aacore-btn-outlined justify-center inline-flex items-center gap-2 w-full sm:w-auto"
      >
        View invoice
        <ChevronRight className="w-4 h-4 shrink-0" />
      </Link>
    </>
  );

  return (
    <div className="rounded-xl border border-amber-200/80 bg-white/70 p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900">
              <FileText className="w-4 h-4 text-amber-700" />
              {toInvoiceLabel(invoice.id)}
            </span>
            <span className="text-sm text-gray-500">from</span>
            <BusinessNameLink invoice={invoice} />
          </div>
          <div className="text-xs font-medium uppercase tracking-wide text-amber-800/80 mt-2">
            Amount due
          </div>
          <div className="mt-0.5 text-2xl font-bold text-gray-900">${formatMoney(amountDue)}</div>
        </div>
        <div className="hidden sm:flex sm:flex-row gap-2 shrink-0">{actionButtons}</div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Invoice sent</dt>
          <dd className="text-gray-900 font-medium mt-0.5">{formatLogDate(sentAt)}</dd>
        </div>
        {resentAt ? (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Invoice resent</dt>
            <dd className="text-gray-900 font-medium mt-0.5">{formatLogDate(resentAt)}</dd>
          </div>
        ) : null}
        {invoice.appointment_id ? (
          <div className={resentAt ? 'sm:col-span-2' : ''}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Appointment</dt>
            <dd className="text-gray-900 font-medium mt-0.5">
              <Link
                to={`/dashboard/customer/appointments/${invoice.appointment_id}`}
                state={{ from: '/dashboard/customer' }}
                className="inline-flex items-center gap-1.5 text-[#0B4778] hover:underline"
              >
                <Calendar className="w-4 h-4 shrink-0" />
                {appointmentWhen}
              </Link>
            </dd>
          </div>
        ) : null}
        {invoice.due_date ? (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Due date</dt>
            <dd className="text-gray-900 font-medium mt-0.5">
              {formatDateOnly(invoice.due_date, 'en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })}
            </dd>
          </div>
        ) : null}
      </dl>

      {invoice.onlinePaymentsEnabled !== true ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900"
        >
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          </div>
          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
            <p className="text-sm font-semibold text-amber-950">Online payments are not enabled</p>
            <p className="mt-1">
              {getBusinessName(invoice)} has not set up online payments through AACore Booking. You will not be able to pay this
              invoice online through the platform.
            </p>
            <p className="mt-2">Please see the note on this invoice for payment instructions from the business.</p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2 pt-1 border-t border-amber-100 sm:hidden">{actionButtons}</div>
    </div>
  );
};

const CustomerPendingInvoicesAlert: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<PendingInvoiceRow[]>([]);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.getMyInvoices({ status: 'SENT', limit: 50, offset: 0 });
      const rows = (resp.data ?? []).filter((r: any) => String(r?.status || '').toUpperCase() === 'SENT');
      setInvoices(rows as PendingInvoiceRow[]);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalDue = useMemo(
    () => invoices.reduce((sum, row) => sum + getAmountDue(row), 0),
    [invoices]
  );

  if (loading || invoices.length === 0) return null;

  const isMulti = invoices.length > 1;
  const primary = invoices[0];

  return (
    <div
      className="mb-4 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm overflow-hidden"
      role="alert"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-bold text-amber-950">
              {isMulti ? 'You have unpaid invoices' : 'You have an unpaid invoice'}
            </h2>
            <p className="text-sm text-amber-900/80 mt-1">
              {isMulti ? (
                `${invoices.length} invoices are awaiting payment — $${formatMoney(totalDue)} total due.`
              ) : (
                <>
                  {toInvoiceLabel(primary.id)} from <BusinessNameLink invoice={primary} className="font-semibold text-[#0B4778] hover:underline transition-colors" /> requires payment.
                </>
              )}
            </p>
          </div>
          {isMulti ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-white/80 text-sm font-semibold text-amber-900 hover:bg-white transition-colors shrink-0"
              aria-expanded={expanded}
            >
              {expanded ? 'Hide' : 'Show all'}
              <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          ) : null}
        </div>

        <div className="mt-4 space-y-3">
          {!isMulti ? (
            <PendingInvoiceDetail invoice={primary} />
          ) : expanded ? (
            invoices.map((invoice) => <PendingInvoiceDetail key={invoice.id} invoice={invoice} />)
          ) : null}
        </div>

        {isMulti ? (
          <div className={`${expanded ? 'mt-4 pt-3 border-t border-amber-200/80' : 'mt-3'} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
            <p className="text-xs text-amber-900/70">
              {expanded
                ? `Showing all ${invoices.length} unpaid invoices.`
                : 'Expand to review invoice sent dates, amounts, and related appointments.'}
            </p>
            <Link
              to="/dashboard/customer/invoices"
              className="text-sm font-semibold text-[#0B4778] hover:underline inline-flex items-center gap-1"
            >
              View all invoices
              <ChevronRight className="w-4 h-4 shrink-0" />
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerPendingInvoicesAlert;
